import { addHmm } from '@/lib/flight-log-utils'
import {
  crewDutyOptions,
  dayNightOptions,
  natureOfFlightOptions,
  pfPmDuties,
  type FlightLogSignOffPhase,
} from '@@api/flightLog.shared'

export type CrewDuty = (typeof crewDutyOptions)[number]
export type DayNight = (typeof dayNightOptions)[number]
export type NatureOfFlight = (typeof natureOfFlightOptions)[number]

export const hasPfPm = (duty: CrewDuty) => (pfPmDuties as readonly string[]).includes(duty)

// Documents stage locally when picked (File + object-URL thumbnail) and only
// upload when the pilot saves a draft or submits; the stored shape is just
// the {s3Key, fileName} pointer.
export interface UploadedFlightLogDocument {
  s3Key: string
  fileName: string
}
export interface PendingFlightLogDocument {
  file: File
  fileName: string
  previewUrl: string
}
export type FlightLogDocumentField = UploadedFlightLogDocument | PendingFlightLogDocument
export const isUploadedDocument = (
  doc: FlightLogDocumentField
): doc is UploadedFlightLogDocument => 's3Key' in doc

// Form state: all sections always present (nullable leaves). Block and flight
// time are real fields — auto-defaulted from the off/on-block and
// take-off/landing clocks but pilot-editable; the remaining derived values
// (new totals, fuel used) are computed at render/save time rather than stored
// in the form.
export interface HoursCellForm {
  broughtFwd: string
  thisLeg: string
}
export interface CyclesCellForm {
  broughtFwd: number
  thisLeg: number | null // null = blank (APU starts unfilled)
}
export interface FlightLogFormData {
  routeTimes: {
    from: string
    to: string
    paxCount: number
    crew: { crewId: string | null; name: string; duty: CrewDuty; pfPm: 'PF' | 'PM' | null }[]
    offBlock: string | null
    onBlock: string | null
    takeOff: string | null
    landing: string | null
    flightTime: string | null
    blockTime: string | null
    distanceNm: number | null
  }
  hours: {
    engineCount: number
    airframe: HoursCellForm
    engines: HoursCellForm[]
    apu: HoursCellForm
    cycles: { engines: CyclesCellForm[]; apu: CyclesCellForm; landings: CyclesCellForm }
  }
  fuel: {
    plannedUpliftL: number | null
    actualUpliftL: number | null
    fuelDocNo: string | null
    departTotalLbs: number | null
    arrivalTotalLbs: number | null
  }
  conditions: {
    dayTime: string
    nightTime: string
    takeOff: DayNight | null
    landing: DayNight | null
    instrumentTime: string | null
    approaches: { type: string; runway: string }[]
  }
  deice: {
    performed: boolean
    fluidType: string | null
    mixRatio: string | null
    startTime: string | null
    stopTime: string | null
  }
  oil: {
    engines: { upliftQts: number | null; remainingQts: number | null }[]
    apuUpliftQts: number | null
    hydUpliftQts: number | null
    psiDif: number | null
  }
  ectm: {
    weightKg: number | null
    oatSatC: number | null
    iasKt: number | null
    tasKt: number | null
    mach: number | null
    flightLevel: number | null
    engines: {
      n1Pct: number | null
      n2Pct: number | null
      itt: number | null
      fuelFlow: number | null
      oilPress: number | null
      oilTemp: number | null
      fuelTemp: number | null
      bleedPress: number | null
    }[]
  }
  inspection: { target: 'Airframe' | 'Engine'; dueHours: string | null; dueDate: string | null }
  defects: {
    hasDefects: boolean
    entries: { description: string; action: 'A' | 'D'; melRef: string | null; actionNotes: string | null }[]
  }
  documents: FlightLogDocumentField[]
  signOff: { preFlight: FlightLogSignOffPhase; postFlight: FlightLogSignOffPhase }
}

// Fixed matrix layout: Airframe, ENG 1-3 and APU
export const FIXED_ENGINE_COUNT = 3

export const emptyHoursCell = (): HoursCellForm => ({ broughtFwd: '0:00', thisLeg: '0:00' })
export const emptyCyclesCell = (): CyclesCellForm => ({ broughtFwd: 0, thisLeg: 1 })
export const emptyOilEngine = () => ({ upliftQts: null, remainingQts: null })
export const emptyEctmEngine = () => ({
  n1Pct: null,
  n2Pct: null,
  itt: null,
  fuelFlow: null,
  oilPress: null,
  oilTemp: null,
  fuelTemp: null,
  bleedPress: null,
})
export const emptySignOffPhase = (): FlightLogSignOffPhase => ({
  crewId: null,
  name: '',
  licenceNo: '',
  signaturePng: null,
  signedAt: null,
})

export function resizeArray<T>(current: T[] | undefined, length: number, make: () => T): T[] {
  const base = current ? current.slice(0, length) : []
  while (base.length < length) base.push(make())
  return base
}

export function buildEmptyForm(engineCount: number): FlightLogFormData {
  return {
    routeTimes: {
      from: '',
      to: '',
      paxCount: 0,
      crew: [],
      offBlock: null,
      onBlock: null,
      takeOff: null,
      landing: null,
      flightTime: null,
      blockTime: null,
      distanceNm: null,
    },
    hours: {
      engineCount,
      airframe: emptyHoursCell(),
      engines: resizeArray([], engineCount, emptyHoursCell),
      apu: emptyHoursCell(),
      cycles: {
        engines: resizeArray([], engineCount, emptyCyclesCell),
        apu: emptyCyclesCell(),
        landings: emptyCyclesCell(),
      },
    },
    fuel: {
      plannedUpliftL: null,
      actualUpliftL: null,
      fuelDocNo: null,
      departTotalLbs: null,
      arrivalTotalLbs: null,
    },
    conditions: {
      dayTime: '0:00',
      nightTime: '0:00',
      takeOff: null,
      landing: null,
      instrumentTime: null,
      approaches: [],
    },
    deice: { performed: false, fluidType: null, mixRatio: null, startTime: null, stopTime: null },
    oil: {
      engines: resizeArray([], engineCount, emptyOilEngine),
      apuUpliftQts: null,
      hydUpliftQts: null,
      psiDif: null,
    },
    ectm: {
      weightKg: null,
      oatSatC: null,
      iasKt: null,
      tasKt: null,
      mach: null,
      flightLevel: null,
      engines: resizeArray([], engineCount, emptyEctmEngine),
    },
    inspection: { target: 'Airframe', dueHours: null, dueDate: null },
    defects: { hasDefects: false, entries: [] },
    documents: [],
    signOff: { preFlight: emptySignOffPhase(), postFlight: emptySignOffPhase() },
  }
}

// Legacy stored shapes (pre-redesign) mapped best-effort at seed time; the new
// shape is written back on the next save.
export interface LegacyConditions {
  dayTakeoffs?: number
  nightTakeoffs?: number
  dayLandings?: number
  nightLandings?: number
}
export interface LegacyOilEngine {
  upliftQts?: number | null
  postShutdownQts?: number | null
  remainingQts?: number | null
}
export interface LegacyPilotSignOff {
  crewId?: string | null
  name?: string
  licenceNo?: string
  signaturePng?: string | null
  signedAt?: string | null
}

export const legacyDayNight = (day?: number, night?: number): DayNight | null =>
  (night ?? 0) > 0 ? 'Night' : (day ?? 0) > 0 ? 'Day' : null

export interface PayloadContext {
  tripLegId: string
  nature: NatureOfFlight | null
  fuelUsed: number | null
}

// Save payload: form values plus the computed columns the schema stores
// (new totals, fuel used).
export function buildFlightLogPayload(v: FlightLogFormData, ctx: PayloadContext) {
  const withTotal = (cell: HoursCellForm) => ({
    broughtFwd: cell.broughtFwd || '0:00',
    thisLeg: cell.thisLeg || '0:00',
    newTotal: addHmm(cell.broughtFwd || '0:00', cell.thisLeg || '0:00') ?? '0:00',
  })
  const withCycleTotal = (cell: CyclesCellForm) => ({
    broughtFwd: cell.broughtFwd,
    thisLeg: cell.thisLeg ?? 0,
    newTotal: cell.broughtFwd + (cell.thisLeg ?? 0),
  })
  return {
    tripLegId: ctx.tripLegId,
    natureOfFlight: ctx.nature,
    routeTimes: {
      ...v.routeTimes,
      crew: v.routeTimes.crew.map((member) => ({
        ...member,
        pfPm: hasPfPm(member.duty) ? member.pfPm : null,
      })),
      // A cleared h:mm input holds '' — store null instead
      blockTime: v.routeTimes.blockTime || null,
      flightTime: v.routeTimes.flightTime || null,
    },
    hours: {
      engineCount: v.hours.engineCount,
      airframe: withTotal(v.hours.airframe),
      engines: v.hours.engines.map(withTotal),
      apu: withTotal(v.hours.apu),
      cycles: {
        engines: v.hours.cycles.engines.map(withCycleTotal),
        apu: withCycleTotal(v.hours.cycles.apu),
        landings: withCycleTotal(v.hours.cycles.landings),
      },
    },
    fuel: { ...v.fuel, usedLbs: ctx.fuelUsed },
    conditions: { ...v.conditions },
    deice: { ...v.deice },
    oil: { ...v.oil },
    ectm: { ...v.ectm },
    inspection: { ...v.inspection },
    defects: { ...v.defects },
    // Pending picks are uploaded (and swapped for pointers) before any save;
    // the filter is a backstop so a File never reaches the JSON payload.
    documents: v.documents.filter(isUploadedDocument),
    signOff: { ...v.signOff },
  }
}

export type FlightLogPayload = ReturnType<typeof buildFlightLogPayload>
